import { ProfileDetailsForm } from "@/components/ProfileDetailsForm";
import { GenderPicker } from "@/components/GenderControl";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDoj, tenureOf } from "@/lib/tenure";
import React, { useEffect, useMemo, useState } from "react";
import { Expandable } from "@/components/Expandable";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import {
    ResponsiveContainer,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    Tooltip as RTooltip,
} from "recharts";
import { api } from "@/lib/api";
import { getLateSlabs, getEarlySlabs, sumLate, sumEarly, shiftMetaOf, shiftKeyOf } from "@/lib/attendanceShifts";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import StatusBadge from "@/components/StatusBadge";
import SessionDialog from "@/components/SessionDialog";
import DeptChips from "@/components/DeptChips";
import { TutorialButton } from "@/components/TutorialModal";
import UserLink from "@/components/UserLink";
import SafeBoundary from "@/components/SafeBoundary";
import { toast } from "sonner";
import {
    ArrowLeft,
    CalendarClock,
    Plus,
    Zap,
    Mail,
    Building2,
    UserCog,
    Paperclip,
    CheckCircle2,
    Clock,
    XCircle,
    AlertTriangle,
    Trash2,
    Home,
    CalendarOff,
    GraduationCap,
    Compass,
    Sparkles,
    Loader2,
    Briefcase,
    Activity,
    BookOpen,
    Trophy,
    Award,
    ShieldOff,
    Star,
    Rocket,
    Camera,
    Wand2,
    Edit3,
    RefreshCw,
    X,
    Upload,
    Target,
    TrendingUp,
    Info,
    ChevronRight,
    Flame,
    Zap as ZapIcon,
    Pencil,
    CalendarDays,
} from "lucide-react";

const EmployeeTimeline = () => {
    const { id } = useParams();
    const { user, checkAuth } = useAuth();
    const { users, depts, ready, refreshUsers } = useData();
    const navigate = useNavigate();
    const [employee, setEmployee] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [roaSessions, setRoaSessions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [selectedRoa, setSelectedRoa] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [showNewRoa, setShowNewRoa] = useState(false);
    const [newDate, setNewDate] = useState(new Date().toISOString().slice(0,10));
    const [newRoaDate, setNewRoaDate] = useState(new Date().toISOString().slice(0,10));
    const [picked, setPicked] = useState([]);
    const [pickedRoa, setPickedRoa] = useState([]);
    const [tab, setTab] = useState("overview");
    const [attendance, setAttendance] = useState([]);
    const [knoRecords, setKnoRecords] = useState([]);
    const [projects, setProjects] = useState([]);
    const [activity, setActivity] = useState([]);
    const [myLearning, setMyLearning] = useState([]);
    const [feedFilter, setFeedFilter] = useState("all");
    const [kudos, setKudos] = useState([]);
    const [restrictedView, setRestrictedView] = useState(false);
    // Gamification stats for the profile owner — powers extra hero tiles
    // (Level, Kudos, Streak) so the header shows something richer than a
    // dry list of counts.
    const [profileStats, setProfileStats] = useState(null);
    // Full org directory — used for manager-name / teammate resolution so
    // leaf employees (whose scoped `users` array only contains themselves)
    // still see their manager & teammates in the hero and right rail.
    const [directory, setDirectory] = useState([]);

    const togglePick = (sid) => setPicked((p) => (p.includes(sid) ? p.filter((x) => x !== sid) : [...p, sid]));
    const togglePickRoa = (sid) => setPickedRoa((p) => (p.includes(sid) ? p.filter((x) => x !== sid) : [...p, sid]));

    // Anyone in the employee's direct manager chain can edit — not just
    // users with role="manager". Lets "employee"-role team leads (e.g.
    // Vandana managing 2-3 directs) create PAS/ROA sessions for their
    // own reports. Backend enforces the same rule on writes.
    const canEdit = user?.role === "super_admin"
        || employee?.manager_id === user?.user_id
        || (employee?.user_id === user?.user_id); // users can edit their own session

    const load = async () => {
        try {
            const se = await api.get(`/sessions?employee_id=${id}`);
            setSessions(se.data);
        } catch {
            // Silently ignore — non-manager viewers get 403 here per
            // the restricted-view rule; UI already hides PAS content.
            setSessions([]);
        }
    };

    const loadRoa = async () => {
        try {
            const r = await api.get(`/roa/sessions?employee_id=${id}`);
            setRoaSessions(r.data || []);
        } catch {
            setRoaSessions([]);
        }
    };

    const loadAttendance = async () => {
        try {
            const r = await api.get(`/attendance?user_id=${id}`);
            setAttendance(r.data || []);
        } catch {
            setAttendance([]);
        }
    };

    const loadKno = async () => {
        try {
            const r = await api.get("/kno");
            setKnoRecords((r.data || []).filter((x) => x.user_id === id));
        } catch {
            setKnoRecords([]);
        }
    };

    const loadProjects = async () => {
        try {
            const r = await api.get(`/projects/user/${id}`);
            setProjects(r.data?.items || []);
        } catch {
            setProjects([]);
        }
    };

    const loadActivity = async () => {
        try {
            const r = await api.get(`/activity/feed?user_id=${id}&limit=100`);
            setActivity(r.data?.items || []);
        } catch {
            setActivity([]);
        }
    };

    const loadMyLearning = async () => {
        // Learning contributions for this profile — resources they authored
        // AND collections they own. Server exposes a flat catalog with
        // `created_by` + `collection_owner_id`, we filter client-side to
        // avoid a bespoke endpoint.
        try {
            const r = await api.get(`/learning/resources`);
            const rows = (r.data || []).filter(
                (x) => x.created_by === id || x.collection_owner_id === id,
            );
            setMyLearning(rows);
        } catch {
            setMyLearning([]);
        }
    };

    const loadKudos = async () => {
        try {
            const r = await api.get(`/users/${id}/kudos`);
            setKudos(r.data?.items || []);
        } catch {
            setKudos([]);
        }
    };

    const loadDirectory = async () => {
        // Full org directory — everyone regardless of the caller's scope.
        // Needed so leaf employees viewing their own profile still see
        // their manager and same-manager/same-dept teammates.
        try {
            const r = await api.get(`/directory`);
            setDirectory(r.data || []);
        } catch {
            setDirectory([]);
        }
    };

    const loadProfileStats = async () => {
        try {
            const r = await api.get(`/users/${id}/stats`);
            setProfileStats(r.data || null);
        } catch {
            setProfileStats(null);
        }
    };

    // Resolve target employee — first from scoped `users` (which gives us
    // full manager-chain visibility). If not found, fall back to the org
    // directory so we can still render a *restricted* profile view (no
    // PAS/ROA info for viewers outside the target's manager chain).
    useEffect(() => {
        const local = users.find((x) => x.user_id === id);
        if (local) {
            setEmployee(local);
            setRestrictedView(false);
            // Hydrate heavy media fields (cover_url) that the list
            // endpoint no longer ships. Non-fatal if it fails — the
            // teal gradient fallback still looks great.
            (async () => {
                try {
                    const r = await api.get(`/users/${id}`);
                    if (r.data?.cover_url) {
                        setEmployee((prev) => ({ ...(prev || {}), cover_url: r.data.cover_url }));
                    }
                } catch { /* ignore */ }
            })();
            return;
        }
        if (!ready) return;
        // Not in caller's scope → fetch the directory card. Everyone in
        // the org can be *viewed* (per the "everyone sees everyone apart
        // from PAS/ROA" rule) but their PAS/ROA endpoints will 403 or
        // return empty, which we honour by hiding those sections.
        (async () => {
            try {
                const r = await api.get(`/directory`);
                const found = (r.data || []).find((u) => u.user_id === id);
                if (found) {
                    setEmployee(found);
                    setRestrictedView(true);
                    // Still try to fetch cover_url via the single-user
                    // endpoint — it uses the same directory projection
                    // for restricted viewers, so cover_url is included.
                    try {
                        const rr = await api.get(`/users/${id}`);
                        if (rr.data?.cover_url) {
                            setEmployee((prev) => ({ ...(prev || {}), cover_url: rr.data.cover_url }));
                        }
                    } catch { /* ignore */ }
                } else {
                    setEmployee(null);
                    setRestrictedView(false);
                }
            } catch {
                setEmployee(null);
                setRestrictedView(false);
            }
        })();
    }, [users, id, ready]);

    useEffect(() => {
        // Load everything the profile can show. PAS/ROA/Attendance/KnO
        // endpoints will silently return empty for out-of-scope viewers,
        // which the UI already handles.
        load();
        loadAttendance();
        loadKno();
        loadRoa();
        loadProjects();
        loadActivity();
        loadMyLearning();
        loadKudos();
        loadDirectory();
        loadProfileStats();
    }, [id]);

    const generate = async () => {
        try {
            const r = await api.post(`/users/${id}/generate-cycles`);
            toast.success(`Generated ${r.data.created} cycles`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const createSession = async () => {
        try {
            const nextCycle = (sessions[sessions.length - 1]?.cycle_number || 0) + 1;
            await api.post(`/sessions`, { employee_id: id, scheduled_date: newDate, cycle_number: nextCycle });
            toast.success("Session created");
            setShowNew(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const deleteOne = async (s) => {
        if (!window.confirm(`Delete Cycle #${s.cycle_number} (${s.scheduled_date})? This cannot be undone.`)) return;
        try {
            await api.delete(`/sessions/${s.id}`);
            toast.success("Session deleted");
            setPicked((p) => p.filter((x) => x !== s.id));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const deletePicked = async () => {
        if (picked.length === 0) return;
        if (!window.confirm(`Delete ${picked.length} session(s)? This cannot be undone.`)) return;
        try {
            const r = await api.post(`/sessions/bulk-delete`, { ids: picked });
            toast.success(`Deleted ${r.data.deleted} session${r.data.deleted!==1?"s":""}`);
            setPicked([]);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const clearTimeline = async () => {
        if (!window.confirm(`Delete the ENTIRE PAS timeline for ${employee?.name}? All ${sessions.length} session(s), notes and attachments will be permanently removed.`)) return;
        if (!window.confirm(`This cannot be undone. Type-free confirmation: are you absolutely sure?`)) return;
        try {
            const r = await api.delete(`/users/${id}/sessions`);
            toast.success(`Cleared ${r.data.deleted} session${r.data.deleted!==1?"s":""}`);
            setPicked([]);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const clearUpcoming = async () => {
        const upcomingCount = sessions.filter(s => s.status === "upcoming").length;
        if (upcomingCount === 0) { toast.info("No upcoming sessions to clear"); return; }
        if (!window.confirm(`Delete ${upcomingCount} upcoming session(s) for ${employee?.name}? Conducted/missed history will be kept.`)) return;
        try {
            const r = await api.delete(`/users/${id}/sessions?status=upcoming`);
            toast.success(`Cleared ${r.data.deleted} upcoming session${r.data.deleted!==1?"s":""}`);
            setPicked([]);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    // ----- ROA actions -----
    const generateRoa = async () => {
        try {
            const r = await api.post(`/roa/users/${id}/generate-cycles`);
            toast.success(`Generated ${r.data.created} ROA cycles`);
            loadRoa();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const createRoaSession = async () => {
        try {
            const nextCycle = (roaSessions[roaSessions.length - 1]?.cycle_number || 0) + 1;
            await api.post(`/roa/sessions`, { employee_id: id, scheduled_date: newRoaDate, cycle_number: nextCycle });
            toast.success("ROA session created");
            setShowNewRoa(false);
            loadRoa();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const deleteOneRoa = async (s) => {
        if (!window.confirm(`Delete ROA Cycle #${s.cycle_number} (${s.scheduled_date})? This cannot be undone.`)) return;
        try {
            await api.delete(`/roa/sessions/${s.id}`);
            toast.success("ROA session deleted");
            setPickedRoa((p) => p.filter((x) => x !== s.id));
            loadRoa();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const deletePickedRoa = async () => {
        if (pickedRoa.length === 0) return;
        if (!window.confirm(`Delete ${pickedRoa.length} ROA session(s)? This cannot be undone.`)) return;
        try {
            const r = await api.post(`/roa/sessions/bulk-delete`, { ids: pickedRoa });
            toast.success(`Deleted ${r.data.deleted} ROA session${r.data.deleted!==1?"s":""}`);
            setPickedRoa([]);
            loadRoa();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const clearRoaUpcoming = async () => {
        const upcomingCount = roaSessions.filter(s => s.status === "upcoming").length;
        if (upcomingCount === 0) { toast.info("No upcoming ROA sessions to clear"); return; }
        if (!window.confirm(`Delete ${upcomingCount} upcoming ROA session(s) for ${employee?.name}?`)) return;
        try {
            const r = await api.delete(`/roa/users/${id}/sessions?status=upcoming`);
            toast.success(`Cleared ${r.data.deleted} upcoming ROA session${r.data.deleted!==1?"s":""}`);
            setPickedRoa([]);
            loadRoa();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    if (!employee) {
        if (!ready) {
            return <div className="text-slate-500 text-sm">Loading…</div>;
        }
        return (
            <div className="max-w-xl mx-auto mt-20 bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center" data-testid="timeline-403">
                <div className="h-14 w-14 rounded-full bg-slate-100 text-slate-500 mx-auto flex items-center justify-center">
                    <ShieldOff className="h-6 w-6" />
                </div>
                <h1 className="font-heading text-xl font-bold text-slate-900 mt-4">Profile not found</h1>
                <p className="text-sm text-slate-500 mt-2">
                    This teammate doesn&apos;t exist or has been removed from the directory.
                </p>
                <button
                    onClick={() => navigate(-1)}
                    className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold"
                    data-testid="timeline-403-back"
                >
                    <ArrowLeft className="h-4 w-4" /> Go back
                </button>
            </div>
        );
    }

    // Resolve manager from the FULL org directory so leaf employees
    // (whose scoped `users` array only holds themselves) still see who
    // they report to. Fall back to `users` while the directory loads.
    // Non-memoised because the arrays involved are small and this runs
    // after an early return that would otherwise trip rules-of-hooks.
    const _managerId = employee?.manager_id;
    const managerRecord = _managerId
        ? ((directory || []).find((u) => u.user_id === _managerId) ||
           users.find((u) => u.user_id === _managerId) ||
           null)
        : null;
    const managerName = managerRecord?.name || "—";
    const today = new Date().toISOString().slice(0,10);

    const stats = {
        conducted: sessions.filter(s=>s.status==="conducted").length,
        upcoming: sessions.filter(s=>s.status==="upcoming" && s.scheduled_date >= today).length,
        overdue: sessions.filter(s=>s.status==="upcoming" && s.scheduled_date < today).length,
        missed: sessions.filter(s=>s.status==="missed").length,
    };

    const roaStats = {
        conducted: roaSessions.filter(s=>s.status==="conducted").length,
        upcoming: roaSessions.filter(s=>s.status==="upcoming" && s.scheduled_date >= today).length,
        overdue: roaSessions.filter(s=>s.status==="upcoming" && s.scheduled_date < today).length,
        missed: roaSessions.filter(s=>s.status==="missed").length,
    };

    const initials = (employee.name || "?").split(/\s/).filter(Boolean).slice(0,2).map(s=>s[0]?.toUpperCase()).join("");

    // Owner or super_admin can edit avatar/cover/headline.
    const isOwner = user?.user_id === employee.user_id;
    const canEditMedia = isOwner || user?.role === "super_admin";

    // Refresh the employee from DataContext so freshly-uploaded media
    // shows up without a full page reload.
    const applyMediaUpdate = async (patch) => {
        try {
            const r = await api.patch(`/users/${employee.user_id}/profile-media`, patch);
            setEmployee((prev) => ({ ...(prev || {}), ...r.data }));
            await refreshUsers?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save");
        }
    };

    // Fallback headline mirrors backend logic — used when the profile
    // hasn't set one yet so the hero is never empty. When the viewer is
    // restricted (no PAS/ROA visibility), we omit those stats from the
    // computed headline too.
    const fallbackHeadline = [
        employee.designation || null,
        !restrictedView && sessions.length > 0 ? `${sessions.length} PAS ${sessions.length === 1 ? "session" : "sessions"}` : null,
        projects.length > 0 ? `${projects.length} ${projects.length === 1 ? "project" : "projects"}` : null,
        myLearning.length > 0 ? `${myLearning.length} learning ${myLearning.length === 1 ? "contribution" : "contributions"}` : null,
    ].filter(Boolean).slice(0, 3).join(" · ") || "New teammate";
    const headline = employee.headline || fallbackHeadline;

    return (
        <div className="space-y-6" data-testid="employee-timeline-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4" /> Back
            </button>

            <div className="relative bg-white border border-slate-200 rounded-2xl shadow-sm">
                {/* Cover — supports a user-uploaded/AI-generated image via
                    `cover_url` or falls back to the teal gradient. `rounded-t-2xl
                    overflow-hidden` on the cover ensures crisp corners without
                    clipping the avatar that overlaps below. */}
                <CoverBanner
                    coverUrl={employee.cover_url}
                    canEdit={canEditMedia}
                    onUpload={(dataUrl) => applyMediaUpdate({ cover_url: dataUrl })}
                    onGenerated={(dataUrl) => applyMediaUpdate({ cover_url: dataUrl })}
                    onClear={() => applyMediaUpdate({ cover_url: "" })}
                    userId={employee.user_id}
                />

                <div className="px-6 sm:px-8 pb-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                            {/* Avatar — circular, overlaps cover. Hover button
                                for owner/admin to upload a new one. */}
                            <AvatarEditable
                                picture={employee.picture}
                                initials={initials}
                                canEdit={canEditMedia}
                                onUpload={(dataUrl) => applyMediaUpdate({ picture: dataUrl })}
                            />
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight" data-testid="timeline-hero-name">{employee.name}</h1>
                            {/* Editable AI-generated headline */}
                            <HeadlineEditor
                                headline={headline}
                                isCustom={!!employee.headline}
                                canEdit={canEditMedia}
                                userId={employee.user_id}
                                onSave={(text) => applyMediaUpdate({ headline: text })}
                                onClear={() => applyMediaUpdate({ headline: "" })}
                            />
                            {/* Sub-line: department + reports-to + email */}
                            <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600">
                                <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4 text-slate-400" /> <DeptChips user={employee} depts={depts} /></span>
                                <span className="inline-flex items-center gap-1.5"><UserCog className="h-4 w-4 text-slate-400" /> Reports to: {managerRecord ? (
                                    <UserLink userId={managerRecord.user_id} name={managerRecord.name} className="text-slate-800 font-medium" testid="hero-reports-to-link" />
                                ) : (<span className="text-slate-800 font-medium">{managerName}</span>)}</span>
                                <a href={`mailto:${employee.email}`} className="inline-flex items-center gap-1.5 hover:text-[#25908A] transition-colors">
                                    <Mail className="h-4 w-4 text-slate-400" /> {employee.email}
                                </a>
                            </div>

                            <ProfileDetailsRow employee={employee} isOwner={isOwner} canSetGender={user?.role === "super_admin"} onSaved={async (data) => { setEmployee((prev) => ({ ...(prev || {}), ...data })); await refreshUsers?.(); await checkAuth?.(); }} />

                            {/* Contextual action row per tab */}
                            {canEdit && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {tab === "pas" && (
                                        <>
                                            <SessionBriefButton userId={employee.user_id} employeeName={employee.name} />
                                            <button onClick={generate} data-testid="generate-cycles-btn" className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full border border-slate-300 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-sm font-semibold transition-colors">
                                                <Zap className="h-4 w-4" /> Generate cycles
                                            </button>
                                            <button onClick={() => setShowNew(true)} data-testid="add-session-btn" className="inline-flex items-center gap-2 h-9 px-4 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-full font-semibold text-sm transition-colors">
                                                <Plus className="h-4 w-4" /> New PAS session
                                            </button>
                                            <TutorialButton
                                                tutorialKey="pas_create"
                                                title="Creating a PAS session"
                                                description="Quick walkthrough on scheduling a Performance Assessment Session."
                                                user={user}
                                            />
                                        </>
                                    )}
                                    {tab === "roa" && (
                                        <>
                                            <button onClick={generateRoa} data-testid="generate-roa-cycles-btn" className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full border border-slate-300 hover:border-indigo-500 hover:text-indigo-600 text-sm font-semibold transition-colors">
                                                <Zap className="h-4 w-4" /> Generate ROA cycles
                                            </button>
                                            <button onClick={() => setShowNewRoa(true)} data-testid="add-roa-session-btn" className="inline-flex items-center gap-2 h-9 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full font-semibold text-sm transition-colors">
                                                <Plus className="h-4 w-4" /> New ROA session
                                            </button>
                                            <TutorialButton
                                                tutorialKey="roa_create"
                                                title="Creating an ROA session"
                                                description="Quick walkthrough on scheduling a Roadmap of Association touchpoint."
                                                user={user}
                                            />
                                        </>
                                    )}
                                    {tab === "overview" && employee?.user_id !== user?.user_id && (
                                        <SessionBriefButton userId={employee.user_id} employeeName={employee.name} />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stats highlights strip — activity-at-a-glance. PAS/ROA
                        tiles hidden for restricted viewers per the visibility
                        rule (only manager chain + super_admin see those).
                        Extra tiles: Level (XP tier), Kudos (peer shoutouts
                        received), Streak (consecutive-day activity). */}
                    <div className={`mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 ${restrictedView ? "lg:grid-cols-3 xl:grid-cols-4" : "lg:grid-cols-4 xl:grid-cols-6"}`} data-testid="timeline-stats-strip">
                        {!restrictedView && <HeroStat icon={CalendarClock} label="PAS Sessions" value={sessions.length} accent="teal" />}
                        {!restrictedView && <HeroStat icon={Compass} label="ROA Sessions" value={roaSessions.length} accent="indigo" />}
                        <HeroStat icon={Briefcase} label="Projects" value={projects.length} accent="amber" />
                        <HeroStat icon={BookOpen} label="Learning" value={myLearning.length} accent="emerald" />
                        <HeroStat
                            icon={Award}
                            label={profileStats?.level_label ? `Lv ${profileStats.level}` : "Level"}
                            value={profileStats ? Number(profileStats.xp || 0).toLocaleString("en-IN") : "—"}
                            accent="violet"
                        />
                        <HeroStat
                            icon={Star}
                            label="Kudos"
                            value={kudos?.length ?? profileStats?.kudos_received ?? 0}
                            accent="rose"
                        />
                        <HeroStat
                            icon={Flame}
                            label="Streak"
                            value={profileStats?.current_streak ? `${profileStats.current_streak}d` : "—"}
                            accent="amber"
                        />
                    </div>
                </div>
            </div>

            {/* Projects chip row — every initiative this person is on / leads.
                Renders inline so it doesn't hog vertical space, keeps the
                timeline itself as the primary content. Clicking a chip
                navigates to /projects where they can drill in. Hidden on
                the Overview tab since the sidebar already surfaces them. */}
            {projects.length > 0 && tab !== "overview" && (
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm px-4 py-3" data-testid="timeline-projects-row">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 flex items-center gap-1.5">
                            <Briefcase className="h-3.5 w-3.5" /> Projects
                            <span className="ml-1 inline-flex items-center h-5 min-w-[20px] px-1.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold">{projects.length}</span>
                        </div>
                        {projects.map((p) => {
                            const isLead = p.lead_id === employee?.user_id;
                            const statusDot = {
                                active: "bg-[#2EB4AC]",
                                planning: "bg-slate-400",
                                on_hold: "bg-amber-400",
                                done: "bg-emerald-500",
                                cancelled: "bg-rose-400",
                            }[p.status] || "bg-slate-300";
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => navigate("/projects")}
                                    data-testid={`timeline-project-chip-${p.id}`}
                                    title={`${p.name}${p.description ? " — " + p.description : ""}`}
                                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200 hover:border-[#2EB4AC] hover:text-[#25908A] hover:bg-teal-50 transition-colors max-w-[240px]"
                                >
                                    <span className={`h-1.5 w-1.5 rounded-full ${statusDot}`} />
                                    {isLead && <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600">Lead</span>}
                                    <span className="truncate">{p.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Tabs — PAS/ROA/Attendance hidden for restricted viewers. */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-2 inline-flex gap-1 flex-wrap">
                <button
                    onClick={() => setTab("overview")}
                    data-testid="employee-tab-overview"
                    className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-medium transition-colors ${
                        tab === "overview" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                >
                    <Activity className="h-4 w-4" /> Overview
                </button>
                {!restrictedView && (
                <button
                    onClick={() => setTab("pas")}
                    data-testid="employee-tab-pas"
                    className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-medium transition-colors ${
                        tab === "pas" ? "bg-[#2EB4AC] text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                >
                    <CalendarClock className="h-4 w-4" /> PAS Sessions
                </button>
                )}
                {!restrictedView && (
                <button
                    onClick={() => setTab("roa")}
                    data-testid="employee-tab-roa"
                    className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-medium transition-colors ${
                        tab === "roa" ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                >
                    <Compass className="h-4 w-4" /> ROA Sessions
                </button>
                )}
                {!restrictedView && (
                <button
                    onClick={() => setTab("attendance")}
                    data-testid="employee-tab-attendance"
                    className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-medium transition-colors ${
                        tab === "attendance" ? "bg-[#2EB4AC] text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                >
                    <Clock className="h-4 w-4" /> Attendance
                </button>
                )}
            </div>

            {tab === "overview" && (
                <ProfileOverview
                    employee={employee}
                    depts={depts}
                    users={users}
                    directory={directory}
                    managerName={managerName}
                    managerRecord={managerRecord}
                    sessions={restrictedView ? [] : sessions}
                    roaSessions={restrictedView ? [] : roaSessions}
                    projects={projects}
                    activity={activity}
                    myLearning={myLearning}
                    attendance={restrictedView ? [] : attendance}
                    kudos={kudos}
                    onKudosChanged={loadKudos}
                    restrictedView={restrictedView}
                    feedFilter={feedFilter}
                    setFeedFilter={setFeedFilter}
                    navigate={navigate}
                    onSwitchTab={setTab}
                    viewerUser={user}
                />
            )}

            {tab === "pas" && (
            <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatBox icon={CheckCircle2} label="Conducted" value={stats.conducted} cls="bg-green-50 text-green-700" />
                <StatBox icon={Clock} label="Upcoming" value={stats.upcoming} cls="bg-blue-50 text-blue-700" />
                <StatBox icon={AlertTriangle} label="Overdue" value={stats.overdue} cls="bg-yellow-50 text-yellow-700" />
                <StatBox icon={XCircle} label="Missed" value={stats.missed} cls="bg-red-50 text-red-700" />
            </div>

            {/* Timeline */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h3 className="font-heading text-xl font-semibold text-slate-900 inline-flex items-center gap-2">
                        <CalendarClock className="h-5 w-5 text-[#2EB4AC]" /> PAS Session Timeline
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-xs text-slate-500">{sessions.length} session{sessions.length!==1?"s":""}</div>
                        {canEdit && sessions.length > 0 && (
                            <>
                                <button
                                    onClick={clearUpcoming}
                                    data-testid="clear-upcoming-btn"
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 hover:border-yellow-400 hover:text-yellow-700 text-sm font-medium text-slate-700 transition-colors"
                                >
                                    <Trash2 className="h-4 w-4" /> Clear upcoming
                                </button>
                                <button
                                    onClick={clearTimeline}
                                    data-testid="clear-timeline-btn"
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors"
                                >
                                    <Trash2 className="h-4 w-4" /> Clear timeline
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {canEdit && picked.length > 0 && (
                    <div className="mb-4 flex items-center justify-between bg-teal-50 border border-teal-100 rounded-lg px-4 py-2">
                        <div className="text-sm text-[#25908A] font-medium">
                            {picked.length} session{picked.length!==1?"s":""} selected
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setPicked([])} className="text-sm text-slate-600 hover:text-slate-900 px-3 h-8">Clear</button>
                            <button
                                onClick={deletePicked}
                                data-testid="delete-selected-sessions-btn"
                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
                            >
                                <Trash2 className="h-4 w-4" /> Delete selected
                            </button>
                        </div>
                    </div>
                )}

                {sessions.length === 0 ? (
                    <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-10 text-center">
                        <CalendarClock className="h-8 w-8 text-[#2EB4AC] mx-auto" />
                        <p className="mt-3 font-heading font-semibold text-slate-900">No sessions yet</p>
                        <p className="text-slate-500 text-sm mt-1">Generate the full year of cycles or add one manually.</p>
                    </div>
                ) : (
                    <div className="relative pl-6">
                        <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-200" />
                        {sessions.map((s) => {
                            const isOverdue = s.status === "upcoming" && s.scheduled_date < today;
                            const displayStatus = isOverdue ? "overdue" : s.status;
                            const isPicked = picked.includes(s.id);
                            return (
                                <div key={s.id} className="relative pb-6 last:pb-0">
                                    <div className={`absolute -left-[18px] top-1 h-3 w-3 rounded-full ring-4 ring-white ${
                                        s.status === "conducted" ? "bg-green-500" :
                                        s.status === "missed" ? "bg-red-500" :
                                        isOverdue ? "bg-yellow-500" : "bg-blue-500"
                                    }`} />
                                    <div
                                        className={`bg-white border rounded-lg p-4 transition-all ${
                                            isPicked ? "border-[#2EB4AC] bg-teal-50/40" : "border-slate-200"
                                        } ${canEdit ? "hover:border-[#2EB4AC] hover:shadow-sm" : ""}`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-start gap-3 min-w-0 flex-1">
                                                {canEdit && (
                                                    <input
                                                        type="checkbox"
                                                        checked={isPicked}
                                                        onChange={(e) => { e.stopPropagation(); togglePick(s.id); }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="mt-1 h-4 w-4 accent-[#2EB4AC] cursor-pointer flex-shrink-0"
                                                        aria-label={`Select cycle ${s.cycle_number}`}
                                                        data-testid={`select-session-${s.id}`}
                                                    />
                                                )}
                                                <button
                                                    onClick={() => canEdit && setSelected(s)}
                                                    disabled={!canEdit}
                                                    className={`text-left flex-1 min-w-0 ${canEdit ? "cursor-pointer" : "cursor-default"}`}
                                                >
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-heading font-semibold text-slate-900">Cycle #{s.cycle_number}</span>
                                                        <StatusBadge status={displayStatus} />
                                                    </div>
                                                    <div className="mt-1 text-sm text-slate-600">
                                                        Scheduled: <span className="text-slate-900 font-medium">{s.scheduled_date}</span>
                                                        {s.conducted_date && <> · Conducted: <span className="text-slate-900 font-medium">{s.conducted_date}</span></>}
                                                    </div>
                                                    {s.notes && <div className="mt-2 text-sm text-slate-600 line-clamp-2">{s.notes}</div>}
                                                    {(s.attachments?.length ?? s.attachments_count ?? 0) > 0 && (
                                                        <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
                                                            <Paperclip className="h-3 w-3" /> {s.attachments?.length ?? s.attachments_count} attachment{(s.attachments?.length ?? s.attachments_count)!==1?"s":""}
                                                        </div>
                                                    )}
                                                </button>
                                            </div>
                                            {canEdit && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); deleteOne(s); }}
                                                    data-testid={`delete-session-${s.id}`}
                                                    className="h-9 w-9 rounded-lg hover:bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0 transition-colors"
                                                    aria-label="Delete session"
                                                    title="Delete this session"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            </>
            )}

            {tab === "attendance" && (
                <div className="space-y-6">
                    <EmployeeAttendance records={attendance} />
                </div>
            )}

            {tab === "roa" && (
                <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatBox icon={CheckCircle2} label="Conducted" value={roaStats.conducted} cls="bg-green-50 text-green-700" />
                    <StatBox icon={Clock} label="Upcoming" value={roaStats.upcoming} cls="bg-indigo-50 text-indigo-700" />
                    <StatBox icon={AlertTriangle} label="Overdue" value={roaStats.overdue} cls="bg-yellow-50 text-yellow-700" />
                    <StatBox icon={XCircle} label="Missed" value={roaStats.missed} cls="bg-red-50 text-red-700" />
                </div>

                <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 mt-6" data-testid="roa-timeline-block">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h3 className="font-heading text-xl font-semibold text-slate-900 inline-flex items-center gap-2">
                            <Compass className="h-5 w-5 text-indigo-600" /> ROA Session Timeline
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-xs text-slate-500">{roaSessions.length} session{roaSessions.length!==1?"s":""}</div>
                            {canEdit && roaSessions.length > 0 && (
                                <button
                                    onClick={clearRoaUpcoming}
                                    data-testid="clear-roa-upcoming-btn"
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 hover:border-yellow-400 hover:text-yellow-700 text-sm font-medium text-slate-700 transition-colors"
                                >
                                    <Trash2 className="h-4 w-4" /> Clear upcoming
                                </button>
                            )}
                        </div>
                    </div>

                    {canEdit && pickedRoa.length > 0 && (
                        <div className="mb-4 flex items-center justify-between bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2">
                            <div className="text-sm text-indigo-700 font-medium">
                                {pickedRoa.length} ROA session{pickedRoa.length!==1?"s":""} selected
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => setPickedRoa([])} className="text-sm text-slate-600 hover:text-slate-900 px-3 h-8">Clear</button>
                                <button
                                    onClick={deletePickedRoa}
                                    data-testid="delete-selected-roa-btn"
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
                                >
                                    <Trash2 className="h-4 w-4" /> Delete selected
                                </button>
                            </div>
                        </div>
                    )}

                    {roaSessions.length === 0 ? (
                        <div className="border-2 border-dashed border-indigo-300 bg-indigo-50/30 rounded-lg p-10 text-center">
                            <Compass className="h-8 w-8 text-indigo-500 mx-auto" />
                            <p className="mt-3 font-heading font-semibold text-slate-900">No ROA sessions yet</p>
                            <p className="text-slate-500 text-sm mt-1">Generate the full year of 6-month cycles, or add one manually.</p>
                        </div>
                    ) : (
                        <div className="relative pl-6">
                            <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-200" />
                            {roaSessions.map((s) => {
                                const isOverdue = s.status === "upcoming" && s.scheduled_date < today;
                                const displayStatus = isOverdue ? "overdue" : s.status;
                                const isPicked = pickedRoa.includes(s.id);
                                return (
                                    <div key={s.id} className="relative pb-6 last:pb-0">
                                        <div className={`absolute -left-[18px] top-1 h-3 w-3 rounded-full ring-4 ring-white ${
                                            s.status === "conducted" ? "bg-green-500" :
                                            s.status === "missed" ? "bg-red-500" :
                                            isOverdue ? "bg-yellow-500" : "bg-indigo-500"
                                        }`} />
                                        <div
                                            className={`bg-white border rounded-lg p-4 transition-all ${
                                                isPicked ? "border-indigo-500 bg-indigo-50/40" : "border-slate-200"
                                            } ${canEdit ? "hover:border-indigo-500 hover:shadow-sm" : ""}`}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                                    {canEdit && (
                                                        <input
                                                            type="checkbox"
                                                            checked={isPicked}
                                                            onChange={(e) => { e.stopPropagation(); togglePickRoa(s.id); }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="mt-1 h-4 w-4 accent-indigo-600 cursor-pointer flex-shrink-0"
                                                            aria-label={`Select ROA cycle ${s.cycle_number}`}
                                                            data-testid={`select-roa-${s.id}`}
                                                        />
                                                    )}
                                                    <button
                                                        onClick={() => canEdit && setSelectedRoa(s)}
                                                        disabled={!canEdit}
                                                        className={`text-left flex-1 min-w-0 ${canEdit ? "cursor-pointer" : "cursor-default"}`}
                                                    >
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-heading font-semibold text-slate-900">ROA Cycle #{s.cycle_number}</span>
                                                            <StatusBadge status={displayStatus} />
                                                        </div>
                                                        <div className="mt-1 text-sm text-slate-600">
                                                            Scheduled: <span className="text-slate-900 font-medium">{s.scheduled_date}</span>
                                                            {s.conducted_date && <> · Conducted: <span className="text-slate-900 font-medium">{s.conducted_date}</span></>}
                                                        </div>
                                                        {s.notes && <div className="mt-2 text-sm text-slate-600 line-clamp-2">{s.notes}</div>}
                                                        {(s.attachments?.length ?? s.attachments_count ?? 0) > 0 && (
                                                            <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
                                                                <Paperclip className="h-3 w-3" /> {s.attachments?.length ?? s.attachments_count} attachment{(s.attachments?.length ?? s.attachments_count)!==1?"s":""}
                                                            </div>
                                                        )}
                                                    </button>
                                                </div>
                                                {canEdit && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); deleteOneRoa(s); }}
                                                        data-testid={`delete-roa-${s.id}`}
                                                        className="h-9 w-9 rounded-lg hover:bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0 transition-colors"
                                                        aria-label="Delete ROA session"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                </>
            )}

            {selected && (
                <SessionDialog session={selected} onClose={() => setSelected(null)} onSaved={() => load()} users={users} />
            )}

            {selectedRoa && (
                <SessionDialog session={selectedRoa} onClose={() => setSelectedRoa(null)} onSaved={() => loadRoa()} users={users} module="roa" />
            )}

            {showNew && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
                    <div className="bg-white border border-slate-200 rounded-lg shadow-lg w-full max-w-md p-6" data-testid="new-pas-modal">
                        <h3 className="font-heading font-semibold text-slate-900 mb-4">New PAS Session</h3>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Scheduled date</label>
                        <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-11 w-full px-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] bg-white" />
                        <div className="mt-6 flex justify-end gap-2">
                            <button onClick={() => setShowNew(false)} className="h-11 px-5 rounded-lg border border-slate-200 text-slate-700">Cancel</button>
                            <button onClick={createSession} className="h-11 px-5 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium">Create</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showNewRoa && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
                    <div className="bg-white border border-slate-200 rounded-lg shadow-lg w-full max-w-md p-6" data-testid="new-roa-modal">
                        <h3 className="font-heading font-semibold text-slate-900 mb-4 inline-flex items-center gap-2">
                            <Compass className="h-4 w-4 text-indigo-600" /> New ROA Session
                        </h3>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Scheduled date</label>
                        <input type="date" value={newRoaDate} onChange={(e) => setNewRoaDate(e.target.value)} className="h-11 w-full px-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white" />
                        <div className="mt-6 flex justify-end gap-2">
                            <button onClick={() => setShowNewRoa(false)} className="h-11 px-5 rounded-lg border border-slate-200 text-slate-700">Cancel</button>
                            <button onClick={createRoaSession} data-testid="confirm-create-roa" className="h-11 px-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium">Create</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

// ---------- HERO STATS ----------
// File-scope helper to crunch a File → base64 data URL, resized to a max
// dimension. Runs entirely in the browser (canvas) so nothing hits the
// server until the user confirms. Rejects non-image files gracefully.
const _readImageAsDataUrl = (file, { maxDim = 1600, maxBytes = 1_800_000, mime = "image/jpeg", quality = 0.82 } = {}) =>
    new Promise((resolve, reject) => {
        if (!file || !file.type?.startsWith("image/")) {
            reject(new Error("Please pick an image file"));
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
                const w = Math.round(img.width * scale);
                const h = Math.round(img.height * scale);
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, w, h);
                let q = quality;
                let dataUrl = canvas.toDataURL(mime, q);
                // Progressive quality drop if we blow the storage cap.
                while (dataUrl.length > maxBytes && q > 0.4) {
                    q -= 0.1;
                    dataUrl = canvas.toDataURL(mime, q);
                }
                if (dataUrl.length > maxBytes) {
                    reject(new Error("Image too large even after compression"));
                    return;
                }
                resolve(dataUrl);
            };
            img.onerror = () => reject(new Error("Couldn't read image"));
            img.src = reader.result;
        };
        reader.onerror = () => reject(new Error("Couldn't read file"));
        reader.readAsDataURL(file);
    });


// ---------- Cover banner with edit affordances ----------
const CoverBanner = ({ coverUrl, canEdit, onUpload, onGenerated, onClear, userId }) => {
    const [busy, setBusy] = useState(false);
    const [genOpen, setGenOpen] = useState(false);
    const inputRef = React.useRef(null);

    const pickFile = () => inputRef.current?.click();

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setBusy(true);
        try {
            const dataUrl = await _readImageAsDataUrl(f, { maxDim: 1800, maxBytes: 1_800_000 });
            await onUpload(dataUrl);
            toast.success("Cover updated");
        } catch (err) {
            toast.error(err?.message || "Couldn't upload");
        } finally { setBusy(false); }
    };

    const backgroundStyle = coverUrl
        ? { backgroundImage: `url(${coverUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
        : { background: "linear-gradient(135deg, #2EB4AC 0%, #25908A 45%, #0f766e 100%)" };

    return (
        <div
            className="h-40 sm:h-48 w-full relative rounded-t-2xl overflow-hidden group"
            style={backgroundStyle}
            data-testid="timeline-hero-banner"
        >
            {!coverUrl && (
                <div
                    className="absolute inset-0 opacity-25 pointer-events-none"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 15% 25%, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0) 40%), radial-gradient(circle at 85% 60%, rgba(255,255,255,0.25) 0px, rgba(255,255,255,0) 45%), radial-gradient(circle at 55% 90%, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0) 35%)",
                    }}
                />
            )}
            {canEdit && (
                <div className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="cover-file-input" />
                    <button
                        onClick={pickFile}
                        disabled={busy}
                        data-testid="cover-upload-btn"
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/90 hover:bg-white text-slate-800 text-xs font-semibold shadow-sm backdrop-blur transition-colors disabled:opacity-60"
                        title="Upload cover image"
                    >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                        Upload
                    </button>
                    <button
                        onClick={() => setGenOpen(true)}
                        data-testid="cover-generate-btn"
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-white text-xs font-semibold shadow-sm transition-colors"
                        style={{ background: "linear-gradient(135deg, #6d28d9 0%, #db2777 100%)" }}
                        title="AI-generate a cover"
                    >
                        <Wand2 className="h-3.5 w-3.5" /> AI
                    </button>
                    {coverUrl && (
                        <button
                            onClick={() => {
                                if (window.confirm("Remove the cover image?")) onClear?.();
                            }}
                            data-testid="cover-clear-btn"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-white/90 hover:bg-rose-50 text-slate-700 hover:text-rose-600 shadow-sm backdrop-blur transition-colors"
                            title="Remove cover"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            )}
            {genOpen && (
                <CoverGenerateModal
                    userId={userId}
                    onClose={() => setGenOpen(false)}
                    onGenerated={async (dataUrl) => {
                        await onGenerated(dataUrl);
                        setGenOpen(false);
                    }}
                />
            )}
        </div>
    );
};

const CoverGenerateModal = ({ userId, onClose, onGenerated }) => {
    const [prompt, setPrompt] = useState("");
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState("");

    const run = async () => {
        setBusy(true);
        setPreview("");
        try {
            const r = await api.post(`/users/${userId}/cover/generate`, { prompt: prompt.trim() });
            setPreview(r.data?.cover_url || "");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally { setBusy(false); }
    };

    return createPortal(
        <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="cover-generate-modal">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3" style={{
                    background: "linear-gradient(135deg, rgba(109,40,217,0.08) 0%, rgba(219,39,119,0.08) 100%)",
                }}>
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 text-white flex items-center justify-center shadow-md">
                        <Wand2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-violet-700 font-semibold">Cover · AI</div>
                        <h3 className="font-heading text-lg font-bold text-slate-900">Generate a profile cover</h3>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="h-8 w-8 rounded-lg hover:bg-white/60 text-slate-500 inline-flex items-center justify-center">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block">Describe the mood (optional)</label>
                    <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        rows={2}
                        placeholder="e.g. calm ocean at dawn · or leave blank for a smart default"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        data-testid="cover-prompt-input"
                        maxLength={200}
                    />
                    {preview && (
                        <div className="rounded-lg overflow-hidden border border-slate-200">
                            <img src={preview} alt="preview" className="w-full h-40 object-cover" data-testid="cover-preview-img" />
                        </div>
                    )}
                    <div className="flex items-center justify-end gap-2 pt-1">
                        <button onClick={onClose} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
                        <button
                            onClick={run}
                            disabled={busy}
                            data-testid="cover-generate-run"
                            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
                            style={{ background: "linear-gradient(135deg, #6d28d9 0%, #db2777 100%)" }}
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            {preview ? "Regenerate" : "Generate"}
                        </button>
                        {preview && (
                            <button
                                onClick={() => onGenerated(preview)}
                                data-testid="cover-use-btn"
                                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold"
                            >
                                Use this cover
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};


// ---------- Editable avatar ----------
const AvatarEditable = ({ picture, initials, canEdit, onUpload }) => {
    const [busy, setBusy] = useState(false);
    const [comicOpen, setComicOpen] = useState(false);
    const inputRef = React.useRef(null);
    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setBusy(true);
        try {
            const dataUrl = await _readImageAsDataUrl(f, { maxDim: 512, maxBytes: 700_000 });
            await onUpload(dataUrl);
            toast.success("Avatar updated");
        } catch (err) {
            toast.error(err?.message || "Couldn't upload");
        } finally { setBusy(false); }
    };
    // Only show the AI comic-ify button when there's an uploaded photo
    // (base64 data URL). Google-sign-in avatars are HTTPS URLs and can't
    // be passed to Nano Banana as a reference — the backend rejects them.
    const canComicify = canEdit && typeof picture === "string" && picture.startsWith("data:image/");
    return (
        <div className="relative inline-block group -mt-16 sm:-mt-20 mb-3">
            <div className="h-32 w-32 sm:h-36 sm:w-36 rounded-full bg-white text-[#25908A] flex items-center justify-center font-heading font-bold text-4xl border-4 border-white shadow-lg ring-1 ring-slate-200 overflow-hidden avatar-hover">
                {picture ? <img src={picture} alt="" className="h-full w-full object-cover" data-testid="hero-avatar-img" /> : initials}
            </div>
            {canEdit && (
                <>
                    <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="avatar-file-input" />
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={busy}
                        data-testid="avatar-upload-btn"
                        className="absolute bottom-1 right-1 h-9 w-9 rounded-full bg-slate-900 hover:bg-slate-800 text-white flex items-center justify-center shadow-md border-2 border-white transition-colors disabled:opacity-60"
                        title="Change avatar"
                        aria-label="Change avatar"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                    </button>
                    {canComicify && (
                        <button
                            type="button"
                            onClick={() => setComicOpen(true)}
                            data-testid="avatar-comicify-btn"
                            title="Turn this photo into an AI comic avatar"
                            className="absolute bottom-1 left-1 h-9 px-2.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-md border-2 border-white transition-transform hover:scale-105"
                            style={{ background: "linear-gradient(135deg, #6d28d9 0%, #db2777 60%, #f59e0b 100%)" }}
                        >
                            <Wand2 className="h-3 w-3" /> Comic-ify
                        </button>
                    )}
                </>
            )}
            {comicOpen && (
                <ComicifyModal
                    onClose={() => setComicOpen(false)}
                    onApply={async (dataUrl) => {
                        await onUpload(dataUrl);
                        setComicOpen(false);
                    }}
                />
            )}
        </div>
    );
};

// ---------- Comic-ify avatar modal ----------
const COMIC_STYLES = [
    { key: "comic",   label: "Comic Book" },
    { key: "anime",   label: "Anime" },
    { key: "pixar",   label: "3D Animated" },
    { key: "sticker", label: "Sticker" },
];

const ComicifyModal = ({ onClose, onApply }) => {
    const { user } = useAuth();
    const [style, setStyle] = useState("comic");
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState("");

    const run = async () => {
        setBusy(true);
        setPreview("");
        try {
            const r = await api.post(`/users/${user.user_id}/avatar/comicify`, { style });
            setPreview(r.data?.picture || "");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Comic-ify failed");
        } finally { setBusy(false); }
    };

    return createPortal(
        <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="comicify-modal">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3" style={{
                    background: "linear-gradient(135deg, rgba(109,40,217,0.10) 0%, rgba(219,39,119,0.10) 50%, rgba(245,158,11,0.10) 100%)",
                }}>
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 via-pink-500 to-amber-400 text-white flex items-center justify-center shadow-md avatar-hover">
                        <Wand2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-violet-700 font-semibold">Avatar · AI</div>
                        <h3 className="font-heading text-lg font-bold text-slate-900">Turn your photo into a comic avatar</h3>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="h-8 w-8 rounded-lg hover:bg-white/60 text-slate-500 inline-flex items-center justify-center">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    {/* Style picker */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Choose a style</div>
                        <div className="flex flex-wrap gap-1.5">
                            {COMIC_STYLES.map((s) => (
                                <button
                                    key={s.key}
                                    onClick={() => setStyle(s.key)}
                                    data-testid={`comicify-style-${s.key}`}
                                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                                        style === s.key
                                            ? "bg-slate-900 text-white border-slate-900"
                                            : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {preview && (
                        <div className="flex justify-center">
                            <img
                                src={preview}
                                alt="preview"
                                className="h-40 w-40 rounded-full object-cover border-4 border-white shadow-lg ring-2 ring-violet-200 avatar-hover"
                                data-testid="comicify-preview-img"
                            />
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2">
                        <button onClick={onClose} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
                        <button
                            onClick={run}
                            disabled={busy}
                            data-testid="comicify-run"
                            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
                            style={{ background: "linear-gradient(135deg, #6d28d9 0%, #db2777 50%, #f59e0b 100%)" }}
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            {preview ? "Regenerate" : "Generate"}
                        </button>
                        {preview && (
                            <button
                                onClick={() => onApply(preview)}
                                data-testid="comicify-apply"
                                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold"
                            >
                                Use this avatar
                            </button>
                        )}
                    </div>
                    {busy && (
                        <div className="text-[11px] text-slate-500 text-center">Rendering your comic avatar with AI — takes about 10-15 seconds…</div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};


// ---------- Editable + AI-generated headline ----------
const HeadlineEditor = ({ headline, isCustom, canEdit, userId, onSave, onClear }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(headline);
    const [busy, setBusy] = useState(false);

    React.useEffect(() => { if (!editing) setDraft(headline); }, [headline, editing]);

    const regen = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/users/${userId}/headline/generate`);
            const text = r.data?.headline || "";
            if (editing) setDraft(text);
            else await onSave(text);
            if (r.data?.source === "fallback") toast.info("AI unavailable — used a computed headline");
            else toast.success("Headline generated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally { setBusy(false); }
    };

    const save = async () => {
        const text = (draft || "").trim();
        if (!text) return;
        await onSave(text);
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="mt-1.5" data-testid="headline-editor">
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    maxLength={220}
                    autoFocus
                    className="w-full px-3 py-2 text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC]"
                    data-testid="headline-input"
                />
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button onClick={save} data-testid="headline-save-btn" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#2EB4AC] hover:bg-[#25908A] text-white text-xs font-semibold">
                        Save
                    </button>
                    <button onClick={() => { setEditing(false); setDraft(headline); }} className="h-8 px-3 rounded-full text-xs text-slate-600 hover:bg-slate-100 font-medium">
                        Cancel
                    </button>
                    <button onClick={regen} disabled={busy} data-testid="headline-regen-btn" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold text-white disabled:opacity-60" style={{ background: "linear-gradient(135deg, #6d28d9 0%, #db2777 100%)" }}>
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                        Regenerate with AI
                    </button>
                    <span className="text-[11px] text-slate-400 tabular-nums ml-auto">{(draft || "").length}/220</span>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-1.5 group inline-flex items-start gap-2 max-w-full" data-testid="headline-display">
            <div className="text-base text-slate-700 font-medium">
                {headline}
                {!isCustom && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold align-middle">Auto</span>}
            </div>
            {canEdit && (
                <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setEditing(true)} data-testid="headline-edit-btn" className="h-7 w-7 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 flex items-center justify-center" title="Edit headline">
                        <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={regen} disabled={busy} data-testid="headline-ai-btn" className="h-7 w-7 rounded-full hover:bg-violet-100 text-violet-600 flex items-center justify-center disabled:opacity-60" title="Regenerate with AI">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                    </button>
                    {isCustom && (
                        <button onClick={onClear} data-testid="headline-clear-btn" className="h-7 w-7 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 flex items-center justify-center" title="Reset to auto-headline">
                            <RefreshCw className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};


// ---------- Milestone ring — progress ring + counter ----------
const MilestoneRing = ({ label, done, total, tone = "teal", icon: Icon, hint, secondaryLabel = "Done" }) => {
    const a = HERO_ACCENTS[tone] || HERO_ACCENTS.teal;
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    // SVG ring — 44 radius, stroke 6, viewbox 100x100. Circumference ≈ 276.
    const circ = 2 * Math.PI * 44;
    const dash = (pct / 100) * circ;
    const stroke = {
        teal: "#2EB4AC",
        indigo: "#6366f1",
        amber: "#f59e0b",
        emerald: "#10b981",
        violet: "#8b5cf6",
        rose: "#f43f5e",
        slate: "#64748b",
    }[tone] || "#2EB4AC";
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid={`milestone-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="relative h-16 w-16 flex-shrink-0">
                <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                    <circle cx="50" cy="50" r="44" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                    <circle
                        cx="50" cy="50" r="44" fill="none"
                        stroke={stroke} strokeWidth="6" strokeLinecap="round"
                        strokeDasharray={`${dash} ${circ}`}
                        style={{ transition: "stroke-dasharray 0.6s ease-out" }}
                    />
                </svg>
                <div className={`absolute inset-0 flex items-center justify-center rounded-full ${a.text}`}>
                    <Icon className="h-5 w-5" />
                </div>
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
                <div className="mt-0.5 flex items-baseline gap-1">
                    <span className="font-heading text-2xl font-bold text-slate-900 tabular-nums">{done}</span>
                    {total > 0 && total !== done && <span className="text-sm text-slate-500 tabular-nums">/ {total}</span>}
                    <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{secondaryLabel}</span>
                </div>
                {hint && <div className="text-[11px] text-slate-500 truncate">{hint}</div>}
            </div>
        </div>
    );
};


// ---------- HERO STATS ----------
const HERO_ACCENTS = {
    teal:    { bg: "bg-teal-50",    ring: "ring-teal-100",    text: "text-[#25908A]" },
    indigo:  { bg: "bg-indigo-50",  ring: "ring-indigo-100",  text: "text-indigo-600" },
    amber:   { bg: "bg-amber-50",   ring: "ring-amber-100",   text: "text-amber-700" },
    emerald: { bg: "bg-emerald-50", ring: "ring-emerald-100", text: "text-emerald-700" },
    violet:  { bg: "bg-violet-50",  ring: "ring-violet-100",  text: "text-violet-700" },
    rose:    { bg: "bg-rose-50",    ring: "ring-rose-100",    text: "text-rose-700" },
    slate:   { bg: "bg-slate-50",   ring: "ring-slate-100",   text: "text-slate-700" },
};

const HeroStat = ({ icon: Icon, label, value, accent = "teal" }) => {
    const a = HERO_ACCENTS[accent] || HERO_ACCENTS.teal;
    return (
        <div
            className={`rounded-xl border border-slate-200 bg-white px-3 py-2.5 flex items-center gap-2.5 hover:border-slate-300 transition-colors`}
            data-testid={`hero-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className={`h-9 w-9 rounded-lg ${a.bg} ${a.text} ring-1 ${a.ring} flex items-center justify-center flex-shrink-0`}>
                <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 truncate">{label}</div>
                <div className="font-heading text-xl font-bold text-slate-900 leading-tight tabular-nums">{value}</div>
            </div>
        </div>
    );
};


// ---------- PROFILE OVERVIEW (LinkedIn-style split) ----------
// Left sticky sidebar with identity & achievements + right column with a
// unified activity feed merged from PAS, ROA, Projects, Learning and
// Gamification events. Kept in-file to preserve the existing single-file
// navigation for this page.

const FEED_TYPES = [
    { key: "all",       label: "All",       icon: Activity },
    { key: "pas",       label: "PAS",       icon: CalendarClock },
    { key: "roa",       label: "ROA",       icon: Compass },
    { key: "project",   label: "Projects",  icon: Briefcase },
    { key: "learning",  label: "Learning",  icon: BookOpen },
    { key: "gamify",    label: "Gamify",    icon: Trophy },
    { key: "attendance",label: "Attendance",icon: Clock },
];

// Map a gamification `event_type` string → our filter bucket.
const gamifyBucket = (event_type = "") => {
    const t = String(event_type).toLowerCase();
    if (t.startsWith("pas") || t.includes("session_conducted") || t.includes("pas_")) return "pas";
    if (t.startsWith("roa") || t.includes("roa_")) return "roa";
    if (t.includes("project")) return "project";
    if (t.includes("learning") || t.includes("resource") || t.includes("note")) return "learning";
    if (t.includes("attendance")) return "attendance";
    return "gamify";
};

const ProfileOverview = ({
    employee,
    depts,
    users,
    directory,
    managerName,
    managerRecord,
    sessions,
    roaSessions,
    projects,
    activity,
    myLearning,
    attendance,
    kudos,
    onKudosChanged,
    restrictedView,
    feedFilter,
    setFeedFilter,
    navigate,
    onSwitchTab,
    viewerUser,
}) => {
    const { user: viewer } = useAuth();
    const isOwner = viewer?.user_id === employee.user_id;
    // Filter chip set — drops PAS/ROA/Attendance when the viewer is
    // outside the target's manager chain.
    const feedTypes = restrictedView
        ? FEED_TYPES.filter((t) => !["pas", "roa", "attendance"].includes(t.key))
        : FEED_TYPES;
    // Merge everything into a single timeline sorted by date desc.
    const events = useMemo(() => {
        const out = [];
        // PAS sessions
        for (const s of sessions) {
            const isConducted = s.status === "conducted";
            out.push({
                id: `pas_${s.id}`,
                type: "pas",
                title: isConducted ? `Completed PAS Cycle #${s.cycle_number}` : `PAS Cycle #${s.cycle_number} · ${s.status}`,
                subtitle: s.notes ? s.notes.slice(0, 140) : `Scheduled ${s.scheduled_date}`,
                date: s.conducted_date || s.scheduled_date,
                icon: CalendarClock,
                tone: "teal",
                onClick: () => onSwitchTab("pas"),
                status: s.status,
            });
        }
        // ROA sessions
        for (const s of roaSessions) {
            const isConducted = s.status === "conducted";
            out.push({
                id: `roa_${s.id}`,
                type: "roa",
                title: isConducted ? `Completed ROA Cycle #${s.cycle_number}` : `ROA Cycle #${s.cycle_number} · ${s.status}`,
                subtitle: s.notes ? s.notes.slice(0, 140) : `Scheduled ${s.scheduled_date}`,
                date: s.conducted_date || s.scheduled_date,
                icon: Compass,
                tone: "indigo",
                onClick: () => onSwitchTab("roa"),
                status: s.status,
            });
        }
        // Projects (joined / leading)
        for (const p of projects) {
            const isLead = p.lead_id === employee.user_id;
            out.push({
                id: `project_${p.id}`,
                type: "project",
                title: isLead ? `Leading “${p.name}”` : `Joined “${p.name}”`,
                subtitle: (p.description || "").slice(0, 140) || `Project status: ${p.status}`,
                date: (p.created_at || "").slice(0, 10),
                icon: Briefcase,
                tone: "amber",
                onClick: () => navigate("/projects"),
                status: p.status,
            });
        }
        // Learning contributions (resources authored or collections owned)
        for (const r of myLearning) {
            out.push({
                id: `learning_${r.id}`,
                type: "learning",
                title: `Contributed to Learning Hub: “${r.name || "Untitled"}”`,
                subtitle: r.collection_name
                    ? `In ${r.collection_name}${r.collection_department_name ? ` · ${r.collection_department_name}` : ""}`
                    : (r.description || "").slice(0, 140),
                date: (r.created_at || "").slice(0, 10),
                icon: BookOpen,
                tone: "emerald",
                onClick: () => navigate("/learning"),
            });
        }
        // Attendance milestones — one row per uploaded month
        for (const rec of (attendance || [])) {
            const late = sumLate(rec);
            const early = sumEarly(rec);
            out.push({
                id: `att_${rec.id || rec.month}`,
                type: "attendance",
                title: `Attendance recorded for ${rec.month}`,
                subtitle: `${rec.total_instances || 0} days · ${late} late · ${early} early exit`,
                date: `${rec.month}-01`,
                icon: Clock,
                tone: "slate",
                onClick: () => onSwitchTab("attendance"),
            });
        }
        // Gamification events (deduped from feed — anything not already
        // covered by the buckets above).
        for (const ev of (activity || [])) {
            const bucket = gamifyBucket(ev.event_type);
            if (bucket === "pas" || bucket === "roa" || bucket === "project" || bucket === "learning") continue;
            out.push({
                id: `evt_${ev.id}`,
                type: bucket,
                title: ev.subject || ev.event_type?.replace(/_/g, " ") || "Activity",
                subtitle: ev.event_type?.replace(/_/g, " ") || "",
                date: (ev.created_at || "").slice(0, 10),
                icon: bucket === "attendance" ? Clock : Trophy,
                tone: bucket === "attendance" ? "slate" : "violet",
            });
        }
        out.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        return out;
    }, [sessions, roaSessions, projects, myLearning, attendance, activity, employee, navigate, onSwitchTab]);

    const visible = feedFilter === "all"
        ? events
        : events.filter((e) => e.type === feedFilter);

    // Collapse-by-default rule — show roughly half of the events (min 5,
    // max 8 to keep the initial fold height sane). "Load all" reveals the
    // rest, "Show less" collapses back. Reset whenever the filter flips
    // so a fresh filter always starts collapsed.
    const [showAllFeed, setShowAllFeed] = useState(false);
    React.useEffect(() => { setShowAllFeed(false); }, [feedFilter]);
    const COLLAPSE_THRESHOLD = 6;
    const collapsedCount = Math.min(8, Math.max(5, Math.ceil(visible.length / 2)));
    const canCollapse = visible.length > COLLAPSE_THRESHOLD;
    const feedRows = canCollapse && !showAllFeed ? visible.slice(0, collapsedCount) : visible;

    const counts = useMemo(() => {
        const c = { all: events.length };
        for (const t of feedTypes) if (t.key !== "all") c[t.key] = 0;
        for (const e of events) if (c[e.type] !== undefined) c[e.type] += 1;
        return c;
    }, [events, feedTypes]);

    // Achievement pills — derived from source data.
    const conductedPas = sessions.filter((s) => s.status === "conducted").length;
    const conductedRoa = roaSessions.filter((s) => s.status === "conducted").length;
    const projectsLed = projects.filter((p) => p.lead_id === employee.user_id).length;
    const badges = [];
    if (conductedPas >= 10) badges.push({ label: "PAS Regular", icon: Star, tone: "teal" });
    if (conductedRoa >= 3)  badges.push({ label: "ROA Champion", icon: Award, tone: "indigo" });
    if (projectsLed >= 1)   badges.push({ label: "Project Lead", icon: Rocket, tone: "amber" });
    if (myLearning.length >= 5) badges.push({ label: "Knowledge Sharer", icon: BookOpen, tone: "emerald" });
    if (badges.length === 0) badges.push({ label: "Getting started", icon: Sparkles, tone: "slate" });

    // Team = manager + peers (same manager) + direct reports. Manages = direct reports.
    const { teammates, directReports } = useMemo(() => {
        const source = (directory && directory.length > 0) ? directory : users;
        const live = (source || []).filter((u) => u.active !== false && u.user_id !== employee.user_id);
        const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
        const reports = live.filter((u) => u.manager_id === employee.user_id).sort(byName);
        const manager = employee.manager_id ? live.find((u) => u.user_id === employee.manager_id) : null;
        const peers = employee.manager_id ? live.filter((u) => u.manager_id === employee.manager_id).sort(byName) : [];
        return {
            teammates: [
                ...(manager ? [{ ...manager, _tag: "Manager" }] : []),
                ...peers,
                ...reports.map((r) => ({ ...r, _tag: "Reports to you" })),
            ],
            directReports: reports,
        };
    }, [directory, users, employee]);

    // Featured learning — top 3 contributions by created_at desc.
    const featuredLearning = useMemo(() => {
        return [...myLearning]
            .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
            .slice(0, 3);
    }, [myLearning]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6" data-testid="profile-overview">
            {/* ------- MAIN COLUMN — stacked LinkedIn-style cards ------- */}
            <div className="space-y-4 min-w-0">
                {/* ABOUT */}
                <SectionCard title="About" testid="section-about">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                        <MetaRow icon={UserCog} label="Reports to" value={
                            managerRecord ? (
                                <UserLink userId={managerRecord.user_id} name={managerRecord.name} testid="about-reports-to-link" />
                            ) : managerName
                        } />
                        <MetaRow icon={Building2} label="Department" value={<DeptChips user={employee} depts={depts} />} />
                        <MetaRow icon={Mail} label="Email" value={<a href={`mailto:${employee.email}`} className="hover:text-[#25908A] break-all">{employee.email}</a>} />
                    </div>
                </SectionCard>

                {/* ACTIVITY */}
                <SectionCard
                    title="Activity"
                    icon={Activity}
                    testid="section-activity"
                    rightSlot={<span className="text-xs text-slate-500 tabular-nums">{visible.length} of {events.length}</span>}
                >
                    {/* Filter chips */}
                    <div className="flex flex-wrap gap-1.5 mb-5" role="tablist" aria-label="Filter activity">
                        {feedTypes.map((t) => {
                            const active = feedFilter === t.key;
                            const Icon = t.icon;
                            const count = counts[t.key] ?? 0;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setFeedFilter(t.key)}
                                    data-testid={`feed-filter-${t.key}`}
                                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold transition-all ${
                                        active
                                            ? "bg-slate-900 text-white border border-slate-900"
                                            : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
                                    }`}
                                >
                                    <Icon className="h-3 w-3" />
                                    {t.label}
                                    <span className={`inline-flex items-center h-4 min-w-[18px] px-1 rounded-full text-[10px] tabular-nums ${
                                        active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"
                                    }`}>{count}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Feed items */}
                    {visible.length === 0 ? (
                        <div className="border-2 border-dashed border-slate-200 rounded-xl p-10 text-center">
                            <Activity className="h-8 w-8 text-slate-300 mx-auto" />
                            <p className="mt-3 font-heading font-semibold text-slate-900">Nothing here yet</p>
                            <p className="text-slate-500 text-sm mt-1">
                                Activity will show up as {employee.name?.split(" ")[0] || "they"} complete sessions, join projects and share learning.
                            </p>
                        </div>
                    ) : (
                        <>
                        <ol className="relative pl-6" data-testid="unified-feed">
                            <span className="absolute left-2 top-2 bottom-2 w-px bg-slate-200" />
                            {feedRows.map((ev) => {
                                const Icon = ev.icon || Activity;
                                const a = HERO_ACCENTS[ev.tone] || HERO_ACCENTS.slate;
                                const clickable = typeof ev.onClick === "function";
                                return (
                                    <li key={ev.id} className="relative pb-4 last:pb-0">
                                        <span className={`absolute -left-[19px] top-1.5 h-3.5 w-3.5 rounded-full ring-4 ring-white ${a.bg} ${a.text} border border-current flex items-center justify-center`}>
                                            <Icon className="h-2 w-2" />
                                        </span>
                                        <button
                                            onClick={ev.onClick}
                                            disabled={!clickable}
                                            data-testid={`feed-item-${ev.id}`}
                                            className={`block w-full text-left border rounded-xl p-3.5 transition-all ${
                                                clickable
                                                    ? "border-slate-200 hover:border-[#2EB4AC] hover:bg-teal-50/40 cursor-pointer"
                                                    : "border-slate-200 cursor-default"
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${a.bg} ${a.text}`}>
                                                            <Icon className="h-2.5 w-2.5" />
                                                            {ev.type}
                                                        </span>
                                                        {ev.status && (
                                                            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                                                                {ev.status}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="mt-1.5 font-semibold text-sm text-slate-900 line-clamp-2">
                                                        {ev.title}
                                                    </div>
                                                    {ev.subtitle && (
                                                        <div className="mt-1 text-xs text-slate-600 line-clamp-2">
                                                            {ev.subtitle}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-slate-400 tabular-nums whitespace-nowrap flex-shrink-0 pt-0.5">
                                                    {ev.date || "—"}
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                        {canCollapse && (
                            <div className="mt-3 flex justify-center">
                                <button
                                    onClick={() => setShowAllFeed((v) => !v)}
                                    data-testid="feed-load-all-btn"
                                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold text-[#25908A] border border-teal-200 hover:border-[#2EB4AC] hover:bg-teal-50 transition-colors"
                                >
                                    {showAllFeed ? (
                                        <>Show less</>
                                    ) : (
                                        <>Load all <span className="tabular-nums opacity-70">({visible.length - feedRows.length} more)</span></>
                                    )}
                                    <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showAllFeed ? "-rotate-90" : "rotate-90"}`} />
                                </button>
                            </div>
                        )}
                        </>
                    )}
                </SectionCard>

                {/* PROJECTS */}
                <SectionCard
                    title="Projects"
                    icon={Briefcase}
                    testid="section-projects"
                    rightSlot={
                        <span className="inline-flex items-center h-5 min-w-[20px] px-1.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold tabular-nums">
                            {projects.length}
                        </span>
                    }
                >
                    {projects.length === 0 ? (
                        <EmptyRow icon={Briefcase} title="No projects yet" hint="Projects joined or led will show up here." />
                    ) : (
                        <div className="space-y-3">
                            {projects.map((p) => {
                                const isLead = p.lead_id === employee.user_id;
                                const dot = {
                                    active: "bg-[#2EB4AC]",
                                    done: "bg-emerald-500",
                                    on_hold: "bg-amber-400",
                                    cancelled: "bg-rose-400",
                                    planning: "bg-slate-400",
                                }[p.status] || "bg-slate-300";
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => navigate("/projects")}
                                        data-testid={`experience-project-${p.id}`}
                                        className="w-full text-left flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-[#2EB4AC] hover:bg-teal-50/30 transition-colors"
                                    >
                                        <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 ring-1 ring-amber-200">
                                            <Briefcase className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                                                {isLead && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800">Lead</span>}
                                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500">
                                                    <span className={`h-1.5 w-1.5 rounded-full ${dot}`} /> {p.status}
                                                </span>
                                            </div>
                                            {p.description && <div className="mt-0.5 text-xs text-slate-600 line-clamp-2">{p.description}</div>}
                                            <div className="mt-1 text-[11px] text-slate-400 tabular-nums">
                                                Since {(p.created_at || "").slice(0, 10) || "—"} · {p.members?.length || 0} teammate{(p.members?.length || 0) === 1 ? "" : "s"}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </SectionCard>

                {/* LEARNING HUB CONTRIBUTIONS — Featured resources / collections
                    this person authored or owns. Pulled from the Learning Hub
                    catalog (db.learning_resources filtered by created_by or
                    collection_owner_id). Top 3 by recency. */}
                {featuredLearning.length > 0 && (
                    <SectionCard
                        title="Knowledge Shared"
                        icon={BookOpen}
                        testid="section-featured"
                        rightSlot={
                            <button onClick={() => navigate("/learning")} className="text-xs text-[#25908A] hover:underline font-semibold">
                                View all {myLearning.length}
                            </button>
                        }
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {featuredLearning.map((r) => (
                                <button
                                    key={r.id}
                                    onClick={() => navigate("/learning")}
                                    data-testid={`featured-learning-${r.id}`}
                                    className="text-left border border-slate-200 rounded-xl p-3 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
                                >
                                    <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700">
                                        <BookOpen className="h-2.5 w-2.5" /> {r.type || "resource"}
                                    </div>
                                    <div className="mt-1.5 font-semibold text-sm text-slate-900 line-clamp-2">{r.name || "Untitled"}</div>
                                    {r.collection_name && (
                                        <div className="mt-1 text-[11px] text-slate-500 truncate">In {r.collection_name}</div>
                                    )}
                                    <div className="mt-1.5 text-[10px] text-slate-400 tabular-nums">{(r.created_at || "").slice(0, 10)}</div>
                                </button>
                            ))}
                        </div>
                    </SectionCard>
                )}

                {/* ODYSSEY MILESTONES — app-context progress rings.
                    PAS conducted / total, ROA conducted / total, projects
                    delivered, learning shared. Replaces the generic "Skills
                    & Achievements" pill row. */}
                <SectionCard title="Odyssey Milestones" icon={Target} testid="section-milestones">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <MilestoneRing
                            label="PAS Cycles"
                            done={conductedPas}
                            total={Math.max(sessions.length, conductedPas)}
                            tone="teal"
                            icon={CalendarClock}
                            hint={sessions.length === 0 ? "No cycles scheduled" : `${sessions.length - conductedPas} pending`}
                        />
                        <MilestoneRing
                            label="ROA Cycles"
                            done={conductedRoa}
                            total={Math.max(roaSessions.length, conductedRoa)}
                            tone="indigo"
                            icon={Compass}
                            hint={roaSessions.length === 0 ? "No cycles scheduled" : `${roaSessions.length - conductedRoa} pending`}
                        />
                        <MilestoneRing
                            label="Projects"
                            done={projectsLed}
                            total={Math.max(projects.length, projectsLed)}
                            tone="amber"
                            icon={Rocket}
                            hint={projects.length === 0 ? "Not on any project yet" : `${projects.length - projectsLed} contributor`}
                            secondaryLabel="Led"
                        />
                        <MilestoneRing
                            label="Learning Shared"
                            done={myLearning.length}
                            total={Math.max(myLearning.length, 10)}
                            tone="emerald"
                            icon={BookOpen}
                            hint={myLearning.length === 0 ? "No contributions yet" : `${myLearning.length} shared`}
                            secondaryLabel="Items"
                        />
                    </div>

                    {/* Contextual badges — displayed as small trophy pills.
                        Only shown when meaningful thresholds are hit. */}
                    {badges.length > 0 && badges[0].label !== "Getting started" && (
                        <div className="mt-4 pt-4 border-t border-slate-100">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2 inline-flex items-center gap-1.5">
                                <Award className="h-3 w-3 text-amber-500" /> Milestones earned
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {badges.map((b, i) => {
                                    const a = HERO_ACCENTS[b.tone] || HERO_ACCENTS.slate;
                                    const Icon = b.icon;
                                    return (
                                        <span
                                            key={i}
                                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-slate-200 ${a.bg} ${a.text}`}
                                        >
                                            <Icon className="h-3 w-3" />
                                            {b.label}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </SectionCard>

                {/* SKILL RADAR — 6-axis snapshot of this person's Odyssey
                    activity shape. Computed client-side from the same source
                    data the profile already loaded (no extra endpoint). */}
                <SectionCard
                    title="Skill Radar"
                    icon={TrendingUp}
                    testid="section-skill-radar"
                    rightSlot={
                        <InfoTooltip testid="skill-radar-info">
                            <div className="font-heading font-bold text-slate-900 text-sm mb-2">What does the radar show?</div>
                            <p className="mb-2">
                                A 6-axis snapshot of how this teammate is showing up on Odyssey today.
                                Every score is computed live from actual activity — no guesswork.
                            </p>
                            <ul className="space-y-1.5">
                                {SKILL_AXES.map((a) => (
                                    <li key={a.key}>
                                        <span className="font-semibold text-slate-900">{a.label}:</span>{" "}
                                        <span className="text-slate-600">{a.hint}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-2 text-[11px] text-slate-500 italic">
                                Scores are capped at 100. Restricted viewers see Cadence &amp; Continuity as
                                <span className="font-semibold">&nbsp;Restricted</span> — those depend on PAS/ROA data
                                only the manager chain can see.
                            </p>
                        </InfoTooltip>
                    }
                >
                    <SkillRadar
                        employee={employee}
                        sessions={sessions}
                        roaSessions={roaSessions}
                        projects={projects}
                        myLearning={myLearning}
                        kudos={kudos}
                        restrictedView={restrictedView}
                    />
                </SectionCard>

                {/* KUDOS WALL — peer shoutouts. Anyone else can send;
                    only recipient / sender / super_admin can delete. */}
                <SectionCard
                    title="Kudos Wall"
                    icon={Award}
                    testid="section-kudos"
                    rightSlot={
                        viewerUser && viewerUser.user_id !== employee.user_id ? (
                            <SendKudosButton
                                targetUserId={employee.user_id}
                                targetName={employee.name}
                                onSent={onKudosChanged}
                            />
                        ) : (
                            <span className="text-xs text-slate-500 tabular-nums">{kudos.length} received</span>
                        )
                    }
                >
                    <KudosList
                        kudos={kudos}
                        viewerUser={viewerUser}
                        targetUserId={employee.user_id}
                        onChanged={onKudosChanged}
                    />
                </SectionCard>
            </div>

            {/* ------- RIGHT RAIL — Teammates & Reports ------- */}
            <aside className="space-y-4 lg:sticky lg:top-6 self-start">
                {/* Team */}
                <SectionCard title="Team" icon={Building2} testid="rail-team" compact rightSlot={<RailCount n={teammates.length} testid="rail-team-count" />}>
                    {teammates.length === 0 ? (
                        <div className="text-xs text-slate-500">No teammates yet.</div>
                    ) : (
                        <div className="space-y-1.5">
                            <Expandable
                                items={teammates}
                                limit={8}
                                className="block w-full pt-1 text-center text-[11px] text-[#25908A] font-semibold"
                                testid="rail-team-more"
                                render={(t) => (
                                    <PersonRow key={t.user_id} user={t} tag={t._tag && (t._tag === "Reports to you" && !isOwner ? `Reports to ${(employee.name || "").split(" ")[0]}` : t._tag)} onClick={() => navigate(`/employees/${t.user_id}`)} testid={`rail-team-${t.user_id}`} />
                                )}
                            />
                        </div>
                    )}
                </SectionCard>

                {/* Direct reports */}
                {directReports.length > 0 && (
                    <SectionCard title="Manages" icon={UserCog} testid="rail-manages" compact rightSlot={<RailCount n={directReports.length} testid="rail-manages-count" />}>
                        <div className="space-y-1.5">
                            <Expandable
                                items={directReports}
                                limit={8}
                                className="block w-full pt-1 text-center text-[11px] text-[#25908A] font-semibold"
                                testid="rail-reports-more"
                                render={(r) => (
                                    <PersonRow key={r.user_id} user={r} onClick={() => navigate(`/employees/${r.user_id}`)} testid={`rail-report-${r.user_id}`} />
                                )}
                            />
                        </div>
                    </SectionCard>
                )}
            </aside>
        </div>
    );
};

// ---------- Reusable section card ----------
const SectionCard = ({ title, icon: Icon, testid, children, rightSlot, compact = false }) => (
    <section
        className={`bg-white border border-slate-200 rounded-2xl shadow-sm ${compact ? "p-4" : "p-5 sm:p-6"}`}
        data-testid={testid}
    >
        <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="font-heading text-lg font-bold text-slate-900 inline-flex items-center gap-2">
                {Icon && <Icon className="h-4 w-4 text-[#2EB4AC]" />}
                {title}
            </h3>
            {rightSlot}
        </div>
        {children}
    </section>
);

const MetaRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
            <div className="mt-0.5 text-slate-800">{value}</div>
        </div>
    </div>
);

const EmptyRow = ({ icon: Icon, title, hint }) => (
    <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
        <Icon className="h-6 w-6 text-slate-300 mx-auto" />
        <p className="mt-2 font-semibold text-slate-900 text-sm">{title}</p>
        {hint && <p className="text-slate-500 text-xs mt-0.5">{hint}</p>}
    </div>
);

const RailCount = ({ n, testid }) => (
    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-teal-50 border border-teal-100 text-[#25908A] text-xs font-bold tabular-nums" data-testid={testid}>{n}</span>
);

const PersonRow = ({ user: u, onClick, testid, tag }) => {
    const initials = (u?.name || u?.email || "?").split(/\s/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join("");
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-teal-50 border border-transparent hover:border-teal-100 transition-colors text-left"
        >
            <div className="h-8 w-8 rounded-full bg-teal-50 text-[#25908A] flex items-center justify-center font-heading font-bold text-xs border border-teal-100 overflow-hidden flex-shrink-0">
                {u.picture ? <img src={u.picture} alt="" className="h-full w-full object-cover" /> : initials}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-semibold text-slate-800 truncate">{u.name || u.email}</span>
                    {tag && (
                        <span className={`shrink-0 px-1.5 h-4 inline-flex items-center rounded text-[9px] font-bold uppercase tracking-wider ${tag === "Manager" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-indigo-50 text-indigo-600 border border-indigo-200"}`}>{tag}</span>
                    )}
                </div>
                {(u.designation || u.sub_department) && (
                    <div className="text-[11px] text-slate-500 truncate">{u.designation || u.sub_department}</div>
                )}
            </div>
        </button>
    );
};


// ---------- Skill Radar ----------
// Six axes derived from the profile's actual Odyssey activity. All maths
// live client-side so we don't hit the LLM on every load — the numbers
// are truthful and reactive. Axes are capped at 100 for readability.
const SKILL_AXES = [
    { key: "cadence",       label: "Cadence",       hint: "How consistently PAS check-ins are conducted — the 45-day heartbeat of the team." },
    { key: "continuity",    label: "Continuity",    hint: "ROA (6-month roadmap) sessions completed — long-term alignment with the org's direction." },
    { key: "leadership",    label: "Leadership",    hint: "Projects led (as project lead) — signal of ownership and steering initiatives." },
    { key: "collaboration", label: "Collaboration", hint: "Unique teammates worked with across projects — cross-team reach." },
    { key: "learning",      label: "Learning",      hint: "Resources authored + collections owned in the Learning Hub — knowledge shared with the team." },
    { key: "recognition",   label: "Recognition",   hint: "Kudos received from peers — external signal of impact." },
];

const SkillRadar = ({ employee, sessions, roaSessions, projects, myLearning, kudos, restrictedView }) => {
    const conductedPas = (sessions || []).filter((s) => s.status === "conducted").length;
    const conductedRoa = (roaSessions || []).filter((s) => s.status === "conducted").length;
    const projectsLed = (projects || []).filter((p) => p.lead_id === employee.user_id).length;
    const uniqueTeammates = React.useMemo(() => {
        const s = new Set();
        for (const p of (projects || [])) {
            for (const mid of (p.member_ids || [])) if (mid !== employee.user_id) s.add(mid);
        }
        return s.size;
    }, [projects, employee.user_id]);

    const clamp = (n) => Math.max(0, Math.min(100, Math.round(n)));

    // Score for each axis, keyed by SKILL_AXES.key. Kept in the same
    // order so the radar polygon and side-list line up.
    const scores = {
        cadence:       clamp(conductedPas * 14),
        continuity:    clamp(conductedRoa * 35),
        leadership:    clamp(projectsLed * 30),
        collaboration: clamp(uniqueTeammates * 12 + (projects || []).length * 8),
        learning:      clamp((myLearning || []).length * 16),
        recognition:   clamp((kudos || []).length * 15),
    };

    // Restricted viewers don't see PAS/ROA numbers → mask Cadence /
    // Continuity axes with a neutral 0 value + a "restricted" flag so
    // the tooltip + side list can render `Restricted` instead.
    const RESTRICTED_AXES = new Set(["cadence", "continuity"]);
    const data = SKILL_AXES.map((a) => {
        const hidden = restrictedView && RESTRICTED_AXES.has(a.key);
        return {
            key: a.key,
            axis: a.label,
            hint: a.hint,
            value: hidden ? 0 : scores[a.key],
            displayValue: hidden ? null : scores[a.key],
            hidden,
        };
    });

    return (
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_240px] gap-4 items-stretch" data-testid="skill-radar-chart">
            {/* Radar fills the card — bigger polygon, tighter to the edges,
                subtle background so the shape reads at a glance. Hover
                lights up the whole card with a futuristic glow + a slow
                sweep animation on the polygon. */}
            <div className="skill-radar-card group relative min-h-[320px] rounded-xl bg-gradient-to-br from-teal-50/60 via-white to-teal-50/30 border border-teal-100/60 p-1 overflow-hidden">
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden>
                    <div className="absolute -inset-1 rounded-2xl bg-[radial-gradient(circle_at_50%_50%,rgba(46,180,172,0.18),transparent_60%)]" />
                    <div className="skill-radar-sweep absolute inset-0" />
                </div>
                <SkillRadarChart data={data} />
            </div>
            {/* Right-side legend — evenly distributes each row along the
                full height so the two columns feel symmetrical. */}
            <ul className="flex flex-col justify-between text-sm min-h-[320px] py-1" data-testid="skill-radar-legend">
                {data.map((d) => (
                    <li
                        key={d.key}
                        className="flex items-center justify-between gap-3 border-b border-slate-100 last:border-0 py-2 hover:bg-teal-50/40 rounded-md px-2 -mx-2 transition-colors cursor-help"
                        title={d.hint}
                    >
                        <span className="font-medium text-slate-700 truncate">{d.axis}</span>
                        {d.hidden ? (
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Restricted</span>
                        ) : (
                            <span className="tabular-nums font-bold text-slate-900">{d.displayValue}</span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

const SkillRadarChart = ({ data }) => {
    return (
        <ResponsiveContainer width="100%" height="100%" minHeight={300}>
            <RadarChart data={data} outerRadius="86%" margin={{ top: 12, right: 20, bottom: 12, left: 20 }}>
                <defs>
                    <linearGradient id="skillRadarFill" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#2EB4AC" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#25908A" stopOpacity={0.25} />
                    </linearGradient>
                </defs>
                <PolarGrid stroke="#cbd5e1" strokeDasharray="2 3" />
                <PolarAngleAxis dataKey="axis" tick={{ fill: "#0f172a", fontSize: 12, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar
                    name="Score"
                    dataKey="value"
                    stroke="#0f766e"
                    strokeWidth={2}
                    fill="url(#skillRadarFill)"
                    fillOpacity={1}
                    dot={{ r: 3, fill: "#2EB4AC", stroke: "#fff", strokeWidth: 1.5 }}
                    activeDot={{ r: 5 }}
                />
                <RTooltip
                    formatter={(v, _n, p) => p?.payload?.hidden ? ["Restricted", ""] : [v, "Score"]}
                    contentStyle={{ borderRadius: 8, borderColor: "#cbd5e1", fontSize: 12 }}
                />
            </RadarChart>
        </ResponsiveContainer>
    );
};


// ---------- Info tooltip (used by section headers) ----------
const InfoTooltip = ({ children, testid }) => {
    const [open, setOpen] = useState(false);
    return (
        <span className="relative inline-flex">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                onBlur={() => setOpen(false)}
                data-testid={testid || "info-tooltip"}
                className="h-6 w-6 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
                aria-label="More info"
                aria-expanded={open}
            >
                <Info className="h-3.5 w-3.5" />
            </button>
            {open && (
                <span
                    role="tooltip"
                    className="absolute right-0 top-full mt-1.5 z-30 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white shadow-xl p-4 text-xs text-slate-700 leading-relaxed"
                >
                    {children}
                </span>
            )}
        </span>
    );
};


// ---------- Kudos: Send button + modal ----------
const SendKudosButton = ({ targetUserId, targetName, onSent }) => {
    const [open, setOpen] = useState(false);
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        const text = msg.trim();
        if (!text) return;
        setBusy(true);
        try {
            await api.post(`/users/${targetUserId}/kudos`, { message: text });
            toast.success("Kudos sent 🎉");
            setMsg("");
            setOpen(false);
            onSent?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send");
        } finally { setBusy(false); }
    };
    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="send-kudos-btn"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold text-white shadow-sm transition-colors"
                style={{ background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)" }}
            >
                <Award className="h-3.5 w-3.5" /> Send Kudos
            </button>
            {open && createPortal(
                <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="kudos-modal">
                        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3" style={{
                            background: "linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(239,68,68,0.12) 100%)",
                        }}>
                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-rose-500 text-white flex items-center justify-center shadow-md">
                                <Award className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold">Kudos</div>
                                <h3 className="font-heading text-lg font-bold text-slate-900 truncate">Recognise {targetName}</h3>
                            </div>
                            <button onClick={() => setOpen(false)} aria-label="Close" className="h-8 w-8 rounded-lg hover:bg-white/60 text-slate-500 inline-flex items-center justify-center">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="p-5 space-y-3">
                            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block">Your kudos message</label>
                            <textarea
                                value={msg}
                                onChange={(e) => setMsg(e.target.value)}
                                rows={3}
                                maxLength={240}
                                autoFocus
                                placeholder="e.g. Amazing work driving the Q3 launch — you kept the team calm and moving!"
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                data-testid="kudos-input"
                            />
                            <div className="flex items-center justify-between">
                                <div className="text-[11px] text-slate-400 tabular-nums">{msg.length}/240</div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setOpen(false)} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
                                    <button
                                        onClick={submit}
                                        disabled={busy || !msg.trim()}
                                        data-testid="kudos-send-btn"
                                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
                                        style={{ background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)" }}
                                    >
                                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />}
                                        Send Kudos
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

const KudosList = ({ kudos, viewerUser, targetUserId, onChanged }) => {
    const remove = async (k) => {
        if (!window.confirm("Remove this kudos?")) return;
        try {
            await api.delete(`/kudos/${k.id}`);
            toast.success("Kudos removed");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't remove");
        }
    };
    if (!kudos || kudos.length === 0) {
        return (
            <EmptyRow icon={Award} title="No kudos yet" hint="Be the first to send a shoutout." />
        );
    }
    return (
        <ul className="space-y-2.5" data-testid="kudos-list">
            {kudos.map((k) => {
                const canDelete = viewerUser && (
                    viewerUser.user_id === k.from_user_id
                    || viewerUser.user_id === targetUserId
                    || viewerUser.role === "super_admin"
                );
                const initials = (k.from_name || "?").split(/\s/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join("");
                return (
                    <li key={k.id} className="border border-slate-200 rounded-xl p-3.5 hover:border-amber-300 transition-colors group" data-testid={`kudos-item-${k.id}`}>
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center font-heading font-bold text-xs border border-amber-100 overflow-hidden flex-shrink-0">
                                {k.from_picture ? <img src={k.from_picture} alt="" className="h-full w-full object-cover" /> : initials}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-sm text-slate-900 truncate">{k.from_name || "Anonymous"}</span>
                                    <span className="text-[11px] text-slate-400 tabular-nums">{(k.created_at || "").slice(0, 10)}</span>
                                </div>
                                <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{k.message}</p>
                            </div>
                            {canDelete && (
                                <button
                                    onClick={() => remove(k)}
                                    data-testid={`kudos-delete-${k.id}`}
                                    className="h-8 w-8 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    aria-label="Remove kudos"
                                    title="Remove"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};


const StatBox = ({ icon: Icon, label, value, cls }) => (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${cls}`}>
            <Icon className="h-4 w-4" />
        </div>
        <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
            <div className="font-heading text-2xl font-bold text-slate-900 leading-tight">{value}</div>
        </div>
    </div>
);

const MONTH_NAMES_EMP = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonthEmp = (ym) => { if (!ym) return "—"; const [y, m] = ym.split("-"); return `${MONTH_NAMES_EMP[parseInt(m,10)-1]} ${y}`; };

const EmployeeAttendance = ({ records }) => {
    const sorted = [...(records || [])].sort((a, b) => b.month.localeCompare(a.month));
    if (sorted.length === 0) {
        return (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
                <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-10 text-center">
                    <Clock className="h-8 w-8 text-[#2EB4AC] mx-auto" />
                    <p className="mt-3 font-heading font-semibold text-slate-900">No attendance records yet</p>
                    <p className="text-slate-500 text-sm mt-1">Monthly rollups uploaded via Control Panel will show up here.</p>
                </div>
            </div>
        );
    }
    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="employee-attendance-panel">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-heading text-xl font-semibold text-slate-900 inline-flex items-center gap-2">
                    <Clock className="h-5 w-5 text-[#2EB4AC]" /> Attendance by month
                </h3>
                <div className="text-xs text-slate-500">{sorted.length} month{sorted.length !== 1 ? "s" : ""}</div>
            </div>
            <div className="space-y-3">
                {sorted.map((r) => {
                    const late = sumLate(r);
                    const early = sumEarly(r);
                    const [l1, l2, l3] = getLateSlabs(r);
                    const [e1, e2, e3] = getEarlySlabs(r);
                    const meta = shiftMetaOf(r);
                    const isFirmware = shiftKeyOf(r) === "firmware";
                    return (
                        <div key={r.id || r.month} className="border border-slate-200 rounded-lg p-4 hover:border-[#2EB4AC] transition-colors" data-testid={`employee-attendance-row-${r.month}`}>
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className="flex items-center gap-3">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-[#25908A] border border-teal-100">
                                        {fmtMonthEmp(r.month)}
                                    </span>
                                    <span
                                        data-testid={`employee-shift-badge-${r.month}`}
                                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border ${
                                            isFirmware
                                                ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                                                : "bg-slate-50 text-slate-600 border-slate-200"
                                        }`}
                                        title={`${meta.label} · ${meta.window}`}
                                    >
                                        {meta.short}
                                    </span>
                                    <div className="text-sm text-slate-500">
                                        Uploaded by <span className="text-slate-700">{(r.uploaded_by || "—").split("@")[0]}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <Pill label="Total" value={r.total_instances || 0} cls="bg-slate-100 text-slate-800" />
                                    <Pill label="Late entry" value={late} cls="bg-yellow-50 text-yellow-800 border-yellow-200" />
                                    <Pill label="Early exit" value={early} cls="bg-red-50 text-red-800 border-red-200" />
                                    <Pill icon={<CalendarOff className="h-3 w-3" />} label="OFF" value={r.oof || 0} cls="bg-amber-50 text-amber-800 border-amber-200" />
                                    <Pill icon={<Home className="h-3 w-3" />} label="WFH" value={r.wfh || 0} cls="bg-blue-50 text-blue-800 border-blue-200" />
                                </div>
                            </div>
                            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                                <MiniStat label={meta.lateLabels[0]} value={l1} />
                                <MiniStat label={meta.lateLabels[1]} value={l2} />
                                <MiniStat label={meta.lateLabels[2]} value={l3} highlight="amber" />
                                <MiniStat label={meta.earlyLabels[0]} value={e1} />
                                <MiniStat label={meta.earlyLabels[1]} value={e2} />
                                <MiniStat label={meta.earlyLabels[2]} value={e3} highlight="red" />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const Pill = ({ label, value, cls, icon }) => (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-transparent ${cls}`}>
        {icon}
        <span className="uppercase tracking-wider text-[10px] opacity-70">{label}</span>
        <span className="font-bold tabular-nums">{value}</span>
    </span>
);

const MiniStat = ({ label, value, highlight }) => {
    const base = "bg-slate-50 border-slate-200 text-slate-700";
    const amber = "bg-yellow-50 border-yellow-200 text-yellow-900";
    const red = "bg-red-50 border-red-200 text-red-900";
    const cls = highlight === "amber" ? amber : highlight === "red" ? red : base;
    return (
        <div className={`border ${cls} rounded-md px-2.5 py-1.5 flex items-center justify-between`}>
            <span className="uppercase tracking-wider text-[10px] opacity-70">{label}</span>
            <span className="font-bold tabular-nums">{value}</span>
        </div>
    );
};


// ---------- Session prep brief — AI 1:1 briefing ----------
// Manager-facing button that pulls last 3 PAS sessions + learning
// progress, then asks Groq for a 5-bullet briefing tailored for the
// upcoming 1:1. Result lands in a modal with a copy-to-clipboard.
const SessionBriefButton = ({ userId, employeeName }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);
    const fetchBrief = async () => {
        setBusy(true);
        setData(null);
        try {
            const r = await api.get(`/ai/session-brief/${userId}`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI brief failed");
            setOpen(false);
        } finally { setBusy(false); }
    };
    const openAndFetch = async () => {
        setOpen(true);
        await fetchBrief();
    };
    const copy = async () => {
        if (!data?.brief) return;
        try {
            await navigator.clipboard.writeText(data.brief);
            toast.success("Brief copied to clipboard");
        } catch { toast.error("Couldn't copy — select and copy manually"); }
    };
    return (
        <>
            <button
                type="button"
                onClick={openAndFetch}
                data-testid="session-brief-btn"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-white text-sm font-semibold transition-all"
                style={{
                    background: "linear-gradient(135deg, #6d28d9 0%, #db2777 50%, #f59e0b 100%)",
                    boxShadow: "0 6px 16px -6px rgba(124,58,237,0.5)",
                }}
                title="Summarise last 3 PAS sessions + current learning progress into a 60-second briefing for your 1:1"
            >
                <Sparkles className="h-4 w-4" /> Summarise for 1:1 · AI
            </button>
            {open && createPortal(
                <div className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3" style={{
                            background: "linear-gradient(135deg, rgba(109,40,217,0.08) 0%, rgba(219,39,119,0.08) 50%, rgba(245,158,11,0.08) 100%)",
                        }}>
                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 via-pink-500 to-amber-400 text-white flex items-center justify-center shadow-md">
                                <Sparkles className="h-5 w-5 drop-shadow" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-violet-700 font-semibold">1:1 Summary · AI</div>
                                <h3 className="font-heading text-lg font-bold text-slate-900 truncate">Your 60-second summary on {employeeName}</h3>
                                <p className="text-[11px] text-slate-500 mt-0.5">Summarises last 3 PAS sessions + current learning progress.</p>
                            </div>
                            <button onClick={() => setOpen(false)} aria-label="Close" className="h-8 w-8 rounded-lg hover:bg-white/60 text-slate-500 inline-flex items-center justify-center">✕</button>
                        </div>
                        <div className="p-5 min-h-[180px]">
                            {busy && (
                                <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                                    <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
                                    Pulling the last 3 sessions + learning progress…
                                </div>
                            )}
                            {!busy && data && (
                                <>
                                    <pre data-testid="session-brief-text" className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-800 bg-violet-50/40 border border-violet-200 rounded-lg p-4">
                                        {data.brief}
                                    </pre>
                                    <div className="mt-3 text-[11px] text-slate-500">
                                        Sourced from <span className="font-semibold text-slate-700 tabular-nums">{data.sources?.pas_sessions ?? 0}</span> PAS session{(data.sources?.pas_sessions ?? 0) === 1 ? "" : "s"}
                                        {" · "}
                                        Learning: <span className="font-semibold text-slate-700 tabular-nums">{data.sources?.learning?.completed ?? 0}</span>/<span className="font-semibold text-slate-700 tabular-nums">{data.sources?.learning?.total ?? 0}</span> completed
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                            <button onClick={fetchBrief} disabled={busy} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 hover:border-slate-300 text-sm font-medium text-slate-700 disabled:opacity-50">
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                                Regenerate
                            </button>
                            <button onClick={copy} disabled={!data?.brief} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold disabled:opacity-50">
                                Copy
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default EmployeeTimeline;

const ProfileDetailsRow = ({ employee, isOwner, onSaved, canSetGender }) => {
    const [open, setOpen] = React.useState(false);
    if (!isOwner && !canSetGender && !employee.designation && !employee.date_of_joining && !employee.gender) return null;
    const chip = "inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-sm";
    return (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="timeline-profile-details">
            {(canSetGender || employee.gender) && (
                <span data-testid="timeline-gender"><GenderPicker u={employee} canEdit={canSetGender} onSaved={onSaved} size="md" /></span>
            )}
            <span className={`${chip} bg-slate-50 border-slate-200 text-slate-700`} data-testid="timeline-designation">
                <Briefcase className="h-3.5 w-3.5 text-[#25908A]" />
                {employee.designation || <span className="text-slate-400 italic">No designation</span>}
            </span>
            <span className={`${chip} bg-amber-50/60 border-amber-200 text-slate-700`} data-testid="timeline-doj">
                <CalendarDays className="h-3.5 w-3.5 text-amber-600" />
                {employee.date_of_joining ? (
                    <>Joined {formatDoj(employee.date_of_joining)} · <span className="font-semibold text-amber-700">{tenureOf(employee.date_of_joining)}</span></>
                ) : (
                    <span className="text-slate-400 italic">Joining date not set</span>
                )}
            </span>
            {isOwner && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="timeline-edit-details-btn"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-dashed border-slate-300 text-xs font-semibold text-slate-500 hover:border-[#2EB4AC] hover:text-[#25908A] transition-colors"
                >
                    <Pencil className="h-3 w-3" /> Edit
                </button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="timeline-edit-details-dialog">
                    <DialogHeader>
                        <DialogTitle>Your profile details</DialogTitle>
                    </DialogHeader>
                    <ProfileDetailsForm user={employee} onSaved={async (d) => { await onSaved(d); setOpen(false); }} />
                </DialogContent>
            </Dialog>
        </div>
    );
};
